import { nowEpochMilliseconds } from "@nijiurachan/js/util/time";
import { ApiError, apiGet, apiPost, getAimgToken } from "@/shared/api/client";

export interface ClientPolicy {
  boardKey: string;
  allowedMimeTypes: string[];
  maxFileSizeBytes: number;
  maxBodyLength: number;
  maxDeleteKeyLength: number;
  threadsPerHour: number;
  threadCreationCooldownSeconds: number;
  replyCreationCooldownSeconds: number;
  threadReplyCreationCooldownSeconds: number;
}

export function getClientPolicy(
  boardKey: string,
  signal?: AbortSignal,
): Promise<ClientPolicy> {
  return apiGet("/client-policy", { boardKey, signal, cache: "no-store" });
}

export interface AppAuthorization {
  clientName: string;
  expiresAt: string;
  csrfToken: string;
}

export function inspectAppAuthorization(
  requestId: string,
  signal: AbortSignal,
): Promise<AppAuthorization> {
  return apiGet(`/app-authorizations/${requestId}`, {
    credentials: "include",
    cache: "no-store",
    signal,
  });
}

export async function waitForBrowserAuthorization(
  signal: AbortSignal,
): Promise<string> {
  const deadline = nowEpochMilliseconds() + 60_000;
  while (true) {
    signal.throwIfAborted();
    try {
      const token = await getAimgToken();
      signal.throwIfAborted();
      return token;
    } catch (error) {
      if (
        !(error instanceof ApiError) ||
        error.status !== 401 ||
        nowEpochMilliseconds() >= deadline
      )
        throw error;
      await new Promise<void>((resolve, reject) => {
        const onAbort = (): void => {
          clearTimeout(timer);
          reject(signal.reason);
        };
        const timer = setTimeout(() => {
          signal.removeEventListener("abort", onAbort);
          resolve();
        }, 1_000);
        signal.addEventListener("abort", onAbort, { once: true });
      });
    }
  }
}

export function decideAppAuthorization(
  requestId: string,
  csrfToken: string,
  decision: "approve" | "deny" | "cancel",
  token: string | undefined,
  signal: AbortSignal,
): Promise<{ redirectUrl: string }> {
  return apiPost(
    `/app-authorizations/${requestId}/decision`,
    { csrfToken, decision },
    { token, credentials: "include", signal, cache: "no-store" },
  );
}
